// ═══════════════════════════════════════════════════════════════
// Handoff — Staff Roster
// Single authority for ICS position management and shift assignments.
// Users must be registered in the global roster (Blackboard).
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Select, Button, Badge,
  SimpleGrid, Modal, TextInput, ActionIcon,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconSun, IconMoon, IconPlus, IconUserCheck, IconTrash,
} from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { primary, semantic } from '../../lib/colors';

export default function StaffRoster() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('handoff');
  const { activeIncident, loadIncidents } = useAuthStore();
  const {
    events, rosterUsers,
    loadAll, getPositionsForEvent,
    assignPosition, clearPositionAssignment,
    createPosition, deletePosition,
  } = useHandoffStore();


  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [addPosOpen, setAddPosOpen] = useState(false);
  const [newPosName, setNewPosName] = useState('');
  const [newPosShort, setNewPosShort] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  useEffect(() => { loadAll(); loadIncidents(); }, []);

  // Auto-select from active incident — match by incidentId first, fall back to name
  useEffect(() => {
    if (selectedEventId || events.length === 0) return;
    if (activeIncident) {
      const match = events.find(e =>
        (activeIncident.id && String(e.incidentId) === String(activeIncident.id))
        || e.eventName === activeIncident.name
      );
      if (match) setSelectedEventId(String(match.id));
    }
  }, [events, activeIncident, selectedEventId]);

  const eventId = selectedEventId;
  const eventPositions = getPositionsForEvent(eventId)
    .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));

  // Build user options from global roster — email as stable key
  const userOptions = rosterUsers
    .filter((u: any) => u.email)
    .map((u: any) => ({
      value: u.email,
      label: u.name || u.fullName || u.email || '(Unknown)',
    }));

  const handleAssign = async (posId: number, shift: 'day' | 'night', email: string | null) => {
    setSaving(true);
    try {
      if (!email) {
        await clearPositionAssignment(posId, shift);
      } else {
        const user = rosterUsers.find(u => u.email === email);
        await assignPosition(posId, shift, user?.fullName || email, email);
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update assignment', color: 'red' });
    }
    setSaving(false);
  };

  const handleAddPosition = async () => {
    if (!newPosName.trim() || !eventId) return;
    setSaving(true);
    const order = String(eventPositions.length + 1);
    await createPosition({ eventId, positionName: newPosName.trim(), shortName: newPosShort.trim(), sortOrder: order });
    setAddPosOpen(false);
    setNewPosName('');
    setNewPosShort('');
    setSaving(false);
    notifications.show({ title: 'Added', message: `Position "${newPosName}" created`, color: 'green' });
  };

  const handleDeletePosition = async (posId: number) => {
    await deletePosition(posId);
    setConfirmDeleteId(null);
    notifications.show({ title: 'Deleted', message: 'Position removed', color: 'gray' });
  };

  const filledDay = eventPositions.filter(p => p.dayShiftName).length;
  const filledNight = eventPositions.filter(p => p.nightShiftName).length;
  const total = eventPositions.length;

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Staff Roster</Title>
          <Text size="sm" c="dimmed">Manage ICS positions and assign registered personnel by shift</Text>
        </div>
        <Group gap="sm">
          <Select
            placeholder="Select event..."
            data={events.map(e => ({ value: String(e.id), label: e.eventName || `Event #${e.id}` }))}
            value={selectedEventId || null}
            onChange={v => setSelectedEventId(v ?? '')}
            searchable
            size="xs"
            w={250}
          />
          {canEdit && eventId && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAddPosOpen(true)}>
              Add Position
            </Button>
          )}
        </Group>
      </Group>

      {!eventId ? (
        <Paper p="xl" withBorder>
          <Stack align="center" gap="sm">
            <Text ta="center" c="dimmed" maw={460}>
              {activeIncident
                ? 'No matching handoff event found for this incident. Create one to set up positions and shift assignments.'
                : 'Select an event from the dropdown above, or choose an active incident in the global nav bar to get started.'}
            </Text>
            <Group>
              <Button size="xs" variant="light" onClick={() => navigate('/handoff/events')}>
                Manage Events
              </Button>
              {!activeIncident && (
                <Button size="xs" variant="subtle" onClick={() => navigate('/incidents')}>
                  Pick an Incident
                </Button>
              )}
            </Group>
          </Stack>
        </Paper>
      ) : (
        <>
          {/* Coverage summary */}
          <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
            <Paper p="sm" withBorder>
              <Text size="xs" c="dimmed">Positions</Text>
              <Text size="xl" fw={700}>{total}</Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Group gap="xs">
                <IconSun size={14} color={semantic.warning} />
                <Text size="xs" c="dimmed">Day Shift Filled</Text>
              </Group>
              <Text size="xl" fw={700}>{filledDay} <Text span size="sm" c="dimmed">/ {total}</Text></Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Group gap="xs">
                <IconMoon size={14} color={primary[700]} />
                <Text size="xs" c="dimmed">Night Shift Filled</Text>
              </Group>
              <Text size="xl" fw={700}>{filledNight} <Text span size="sm" c="dimmed">/ {total}</Text></Text>
            </Paper>
          </SimpleGrid>

          {/* Position roster */}
          {eventPositions.length === 0 ? (
            <Paper p="xl" withBorder>
              <Text ta="center" c="dimmed">No positions defined for this event. Default ICS positions will be created automatically.</Text>
            </Paper>
          ) : (
            <Stack gap="sm">
              {eventPositions.map(p => {
                const dayVal = p.dayShiftEmail || null;
                const nightVal = p.nightShiftEmail || null;

                // Ensure assigned users appear in options even if no longer in roster
                const dayOpts = dayVal && !userOptions.find(o => o.value === dayVal)
                  ? [...userOptions, { value: dayVal, label: p.dayShiftName || dayVal }]
                  : userOptions;
                const nightOpts = nightVal && !userOptions.find(o => o.value === nightVal)
                  ? [...userOptions, { value: nightVal, label: p.nightShiftName || nightVal }]
                  : userOptions;

                return (
                  <Paper key={p.id} p="md" withBorder>
                    <Group justify="space-between" mb="sm">
                      <Group gap="xs">
                        <Text fw={600}>{p.positionName}</Text>
                        {p.shortName && <Badge size="xs" variant="outline" color="gray">{p.shortName}</Badge>}
                      </Group>
                      <Group gap="xs">
                        <Badge size="xs" color={
                          (p.dayShiftName && p.nightShiftName) ? 'green' :
                          (p.dayShiftName || p.nightShiftName) ? 'yellow' : 'red'
                        }>
                          {(p.dayShiftName && p.nightShiftName) ? 'Fully Staffed' :
                           (p.dayShiftName || p.nightShiftName) ? 'Partial' : 'Unstaffed'}
                        </Badge>
                        {canEdit && (
                          confirmDeleteId === p.id ? (
                            <Group gap={4}>
                              <Button size="compact-xs" color="red" onClick={() => handleDeletePosition(p.id)}>Delete</Button>
                              <Button size="compact-xs" variant="subtle" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                            </Group>
                          ) : (
                            <ActionIcon variant="subtle" color="red" size="sm"
                              onClick={() => setConfirmDeleteId(p.id)} title="Remove position">
                              <IconTrash size={14} />
                            </ActionIcon>
                          )
                        )}
                      </Group>
                    </Group>

                    <SimpleGrid cols={{ base: 1, sm: 2 }}>
                      {/* Day shift */}
                      <div>
                        <Group gap={4} mb={4}>
                          <IconSun size={12} color={semantic.warning} />
                          <Text size="xs" fw={600}>Day Shift</Text>
                        </Group>
                        {canEdit ? (
                          <Select
                            placeholder="Assign person..."
                            data={dayOpts}
                            value={dayVal}
                            onChange={v => handleAssign(p.id, 'day', v)}
                            searchable clearable
                            size="xs"
                            disabled={saving}
                          />
                        ) : (
                          <Group gap="xs">
                            {p.dayShiftName ? (
                              <>
                                <IconUserCheck size={14} color={semantic.success} />
                                <Text size="sm">{p.dayShiftName}</Text>
                              </>
                            ) : (
                              <Text size="sm" c="dimmed">Unassigned</Text>
                            )}
                          </Group>
                        )}
                      </div>

                      {/* Night shift */}
                      <div>
                        <Group gap={4} mb={4}>
                          <IconMoon size={12} color={primary[700]} />
                          <Text size="xs" fw={600}>Night Shift</Text>
                        </Group>
                        {canEdit ? (
                          <Select
                            placeholder="Assign person..."
                            data={nightOpts}
                            value={nightVal}
                            onChange={v => handleAssign(p.id, 'night', v)}
                            searchable clearable
                            size="xs"
                            disabled={saving}
                          />
                        ) : (
                          <Group gap="xs">
                            {p.nightShiftName ? (
                              <>
                                <IconUserCheck size={14} color={semantic.success} />
                                <Text size="sm">{p.nightShiftName}</Text>
                              </>
                            ) : (
                              <Text size="sm" c="dimmed">Unassigned</Text>
                            )}
                          </Group>
                        )}
                      </div>
                    </SimpleGrid>
                  </Paper>
                );
              })}
            </Stack>
          )}

          <Text size="xs" c="dimmed" mt="md">
            Only registered Ignatius users can be assigned. To add new personnel, create their account in Settings &gt; Users.
          </Text>
        </>
      )}

      {/* Add Position Modal */}
      <Modal opened={addPosOpen} onClose={() => setAddPosOpen(false)} title="Add Position" size="sm">
        <Stack gap="sm">
          <TextInput label="Position Name" placeholder="e.g., Operations Section Chief"
            value={newPosName} onChange={e => setNewPosName(e.target.value)} required />
          <TextInput label="Short Name" placeholder="e.g., OPS"
            value={newPosShort} onChange={e => setNewPosShort(e.target.value)} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddPosOpen(false)}>Cancel</Button>
            <Button onClick={handleAddPosition} loading={saving} disabled={!newPosName.trim()}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
